import type { GridJourney, GridTask, SortMode } from "../types";
import { deriveCellState } from "./cellState";

/**
 * Apply the user's chosen sort mode to the visible journeys array.
 *
 * Each sort key is computed from the in-memory grid data so we don't need to
 * round-trip to the backend for "Most overdue" or "Least complete". `custom`
 * just uses the order the caller passes in (i.e. the manual journey_order
 * preference), preserved across automatic sort flips.
 */
export function sortJourneys(
  journeys: GridJourney[],
  tasks: GridTask[],
  sortKey: SortMode,
  customOrder: GridJourney[],
  now: Date = new Date(),
): GridJourney[] {
  if (sortKey === "custom") return customOrder;

  // Build per-journey rollups in one pass so each sort mode is cheap.
  const rollups = new Map<string, { maxDaysLate: number; total: number; done: number }>();
  journeys.forEach((j) => rollups.set(String(j.id), { maxDaysLate: 0, total: 0, done: 0 }));

  tasks.forEach((task) => {
    const stat = rollups.get(String(task.newHireJourneyId));
    if (!stat) return;
    stat.total += 1;
    const state = deriveCellState(task, now);
    if (state.kind === "done") stat.done += 1;
    if (state.kind === "overdue" && state.daysLate > stat.maxDaysLate) {
      stat.maxDaysLate = state.daysLate;
    }
  });

  const sorted = [...journeys];

  switch (sortKey) {
    case "most_overdue":
      sorted.sort((a, b) => {
        const aStat = rollups.get(String(a.id))!;
        const bStat = rollups.get(String(b.id))!;
        return bStat.maxDaysLate - aStat.maxDaysLate;
      });
      break;
    case "least_complete":
      sorted.sort((a, b) => {
        const aStat = rollups.get(String(a.id))!;
        const bStat = rollups.get(String(b.id))!;
        const aPct = aStat.total > 0 ? aStat.done / aStat.total : 1;
        const bPct = bStat.total > 0 ? bStat.done / bStat.total : 1;
        return aPct - bPct;
      });
      break;
    case "start_date_asc":
      sorted.sort((a, b) => compareNullable(a.startDate, b.startDate));
      break;
    case "start_date_desc":
      sorted.sort((a, b) => compareNullable(b.startDate, a.startDate));
      break;
    case "name_asc":
      sorted.sort((a, b) => compareNullable(a.newHire?.fullName, b.newHire?.fullName));
      break;
    case "name_desc":
      sorted.sort((a, b) => compareNullable(b.newHire?.fullName, a.newHire?.fullName));
      break;
    case "custom":
    default:
      break;
  }

  return sorted;
}

function compareNullable(a: string | null | undefined, b: string | null | undefined): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a.localeCompare(b);
}
